import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Settings, 
  MessageSquare, 
  FileText, 
  ShoppingCart, 
  PenTool, 
  Headphones, 
  Search, 
  Image, 
  Globe, 
  Heart, 
  Users, 
  BarChart3, 
  Zap, 
  TestTube, 
  Save, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Eye, 
  EyeOff,
  Bot,
  Sparkles,
  Target,
  Mail,
  Share2
} from 'lucide-react';
import { adminAPI } from '../../services/api';

const AIManagement = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [testConnectionLoading, setTestConnectionLoading] = useState(false);
  const [testConnectionResult, setTestConnectionResult] = useState(null);
  const [aiSettings, setAiSettings] = useState({
    openai_api_key: '',
    ai_enabled: false,
    ai_chat_enabled: false,
    ai_chat_source_welcome_enabled: true,
    ai_chat_source_site_content_enabled: false,
    ai_content_generation_enabled: false,
    ai_product_descriptions_enabled: false,
    ai_blog_writer_enabled: false,
    ai_customer_support_enabled: false,
    ai_seo_optimization_enabled: false,
    ai_image_generation_enabled: false,
    ai_translation_enabled: false,
    ai_sentiment_analysis_enabled: false,
    ai_personalization_enabled: false,
    ai_recommendations_enabled: false,
    ai_analytics_enabled: false,
    ai_automation_enabled: false,
    ai_chat_model: 'gpt-3.5-turbo',
    ai_content_model: 'gpt-4',
    ai_max_tokens: '1000',
    ai_temperature: '0.7',
    ai_chat_welcome_message: 'Hello! I am an AI assistant for Niswa Info Tech. How can I help you today?',
    ai_content_prompt_template: 'You are an expert content creator specializing in technology and digital marketing.',
    ai_product_description_template: 'You are an expert copywriter specializing in product descriptions.',
    ai_blog_writer_prompt: 'You are an expert content writer specializing in technology and digital marketing.',
    ai_customer_support_prompt: 'You are a helpful customer support representative for Niswa Info Tech.',
    ai_seo_prompt_template: 'You are an SEO expert. Create content optimized for search engines.',
    ai_translation_languages: '["en", "es", "fr", "de", "it", "pt", "ru", "ja", "ko", "zh", "ar", "hi"]',
    ai_personalization_rules: '{"user_preferences": true, "browsing_history": true, "purchase_history": true, "location_based": true}',
    ai_recommendation_algorithm: 'collaborative_filtering',
    ai_analytics_insights: '{"trend_analysis": true, "anomaly_detection": true, "predictive_analytics": true, "performance_optimization": true}',
    ai_automation_rules: '{"email_automation": true, "content_scheduling": true, "social_media_posting": true, "customer_segmentation": true}',
    ai_chat_max_conversations: '50',
    ai_chat_session_timeout: '30',
    ai_content_max_length: '2000',
    ai_content_quality_threshold: '0.8',
    ai_response_timeout: '30',
    ai_rate_limit_per_minute: '60',
    ai_backup_model: 'gpt-3.5-turbo',
    ai_fallback_enabled: true,
    ai_logging_enabled: true,
    ai_debug_mode: false
  });

  const [contentGenerationForm, setContentGenerationForm] = useState({
    type: 'blog_post',
    prompt: '',
    context: ''
  });
  const [generatedContent, setGeneratedContent] = useState('');

  const [imageGenerationForm, setImageGenerationForm] = useState({
    prompt: '',
    model: 'dall-e-3',
    size: '1024x1024',
    customSize: '',
    quality: 'standard',
    style: 'vivid'
  });
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageValidationMessage, setImageValidationMessage] = useState('');

  // Fetch AI settings
  const fetchAISettings = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAISettings();
      if (response.data.success) {
        setAiSettings(prev => ({ ...prev, ...response.data.data }));
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save AI settings
  const saveAISettings = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.updateAISettings(aiSettings);
      if (response.data.success) {
        setSaveMessage('AI settings saved successfully!');
        setSaveStatus('success');
        setTimeout(() => {
          setSaveMessage('');
          setSaveStatus('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving AI settings:', error);
      setSaveMessage('Error saving AI settings');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus('');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // Test OpenAI connection
  const testConnection = async () => {
    setTestConnectionLoading(true);
    try {
      const response = await adminAPI.testAIConnection();
      if (response.data.success) {
        setTestConnectionResult({
          success: true,
          message: 'Connection successful!',
          data: response.data.data
        });
      }
    } catch (error) {
      setTestConnectionResult({
        success: false,
        message: 'Connection failed',
        error: error.response?.data?.message || error.message
      });
    } finally {
      setTestConnectionLoading(false);
    }
  };

  // Generate content
  const generateContent = async () => {
    if (!contentGenerationForm.prompt.trim()) return;
    
    setLoading(true);
    try {
      // Auto-select model if 'auto' is selected
      let selectedModel = aiSettings.ai_content_model;
      if (selectedModel === 'auto') {
        selectedModel = getAutoSelectedModel(contentGenerationForm.type, contentGenerationForm.prompt);
      }
      
      const requestData = {
        ...contentGenerationForm,
        model: selectedModel
      };
      
      const response = await adminAPI.generateContent(requestData);
      if (response.data.success) {
        setGeneratedContent(response.data.data.content);
      }
    } catch (error) {
      console.error('Error generating content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate image
  const generateImage = async () => {
    if (!imageGenerationForm.prompt.trim()) {
      setImageValidationMessage('Please enter a prompt for image generation');
      return;
    }
    
    setImageValidationMessage('');
    
    // Validate custom size if selected
    if (imageGenerationForm.size === 'custom') {
      if (!imageGenerationForm.customSize.trim()) {
        setImageValidationMessage('Please enter a custom size');
        return;
      }
      
      // Validate custom size format
      const sizeRegex = /^\d+x\d+$/;
      if (!sizeRegex.test(imageGenerationForm.customSize)) {
        setImageValidationMessage('Custom size must be in format: widthxheight (e.g., 1024x1024)');
        return;
      }
      
      // Validate size values
      const [width, height] = imageGenerationForm.customSize.split('x').map(Number);
      if (width < 1 || height < 1 || width > 2048 || height > 2048) {
        setImageValidationMessage('Custom size dimensions must be between 1 and 2048 pixels');
        return;
      }
      
      // Validate aspect ratio for DALL-E 2
      if (imageGenerationForm.model === 'dall-e-2' && width !== height) {
        setImageValidationMessage('DALL-E 2 only supports square images. Use equal width and height (e.g., 512x512)');
        return;
      }
      
      // Validate aspect ratio for DALL-E 3
      if (imageGenerationForm.model === 'dall-e-3') {
        const aspectRatio = width / height;
        const validRatios = [1, 16/9, 9/16]; // 1:1, 16:9, 9:16
        const isValidRatio = validRatios.some(ratio => Math.abs(aspectRatio - ratio) < 0.01);
        
        if (!isValidRatio) {
          setImageValidationMessage('DALL-E 3 only supports aspect ratios: 1:1 (square), 16:9 (landscape), or 9:16 (portrait)');
          return;
        }
      }
    }
    
    setLoading(true);
    try {
      // Prepare the data to send
      const requestData = {
        ...imageGenerationForm,
        size: imageGenerationForm.size === 'custom' ? imageGenerationForm.customSize : imageGenerationForm.size
      };
      
      const response = await adminAPI.generateImage(requestData);
      if (response.data.success) {
        setGeneratedImage(response.data.data);
        setImageValidationMessage('');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      setImageValidationMessage(error.response?.data?.message || 'Error generating image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-select best model based on content type and requirements
  const getAutoSelectedModel = (contentType, prompt = '') => {
    const promptLower = prompt.toLowerCase();
    
    // Check for specific requirements in prompt
    const hasImageGeneration = promptLower.includes('image') || promptLower.includes('picture') || promptLower.includes('visual') || promptLower.includes('photo');
    const hasVision = promptLower.includes('analyze') || promptLower.includes('describe image') || promptLower.includes('what do you see');
    const hasLongContent = promptLower.length > 500 || contentType === 'whitepaper' || contentType === 'case_study';
    const hasComplexTask = promptLower.includes('analyze') || promptLower.includes('research') || promptLower.includes('detailed');
    const hasCreativeTask = promptLower.includes('creative') || promptLower.includes('story') || promptLower.includes('narrative');
    const hasCodeTask = promptLower.includes('code') || promptLower.includes('programming') || promptLower.includes('script');
    
    // Model selection logic
    if (hasImageGeneration) {
      return 'dall-e-3'; // Best for image generation
    }
    
    if (hasVision) {
      return 'gpt-4-vision-preview'; // Best for image analysis
    }
    
    if (hasLongContent || hasComplexTask) {
      return 'gpt-4o'; // Best for long, complex content
    }
    
    if (hasCreativeTask) {
      return 'gpt-4o'; // Best for creative writing
    }
    
    if (hasCodeTask) {
      return 'gpt-4o'; // Best for code generation
    }
    
    // Content type based selection
    switch (contentType) {
      case 'blog_post':
      case 'whitepaper':
      case 'case_study':
      case 'press_release':
        return 'gpt-4o'; // High quality for important content
      
      case 'product_description':
      case 'ad_copy':
      case 'video_script':
        return 'gpt-4o'; // Creative and persuasive content
      
      case 'seo_content':
      case 'meta_description':
        return 'gpt-4o-mini'; // Fast and cost-effective for SEO
      
      case 'email_template':
      case 'social_media':
      case 'newsletter':
        return 'gpt-3.5-turbo'; // Fast and reliable for regular content
      
      case 'faq_content':
      case 'testimonial':
        return 'gpt-3.5-turbo'; // Simple, straightforward content
      
      default:
        return 'gpt-4o'; // Default to best quality
    }
  };

  // Auto-select best chat model
  const getAutoSelectedChatModel = () => {
    // For chat, we want a balance of speed and quality
    // GPT-4o is the best overall choice for chat
    return 'gpt-4o';
  };

  // Handle setting changes
  const handleSettingChange = (key, value) => {
    setAiSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  useEffect(() => {
    fetchAISettings();
  }, []);

  const tabs = [
    { id: 'settings', label: 'AI Settings', icon: Settings },
    { id: 'content', label: 'Content Generation', icon: FileText },
    { id: 'image', label: 'Image Generation', icon: Image },
    { id: 'features', label: 'AI Features', icon: Sparkles }
  ];

  const modelOptions = [
    // Auto Selection
    { value: 'auto', label: '🔄 Auto (Smart Model Selection)' },
    // Latest GPT-5 Models (2024-2025)
    { value: 'gpt-5', label: 'GPT-5 (Latest & Most Advanced - 2024)' },
    { value: 'gpt-5-turbo', label: 'GPT-5 Turbo (High Performance - 2024)' },
    { value: 'gpt-5o', label: 'GPT-5o (Latest Omni Model - 2024)' },
    { value: 'gpt-5o-mini', label: 'GPT-5o Mini (Fast & Cost-effective - 2024)' },
    // Latest GPT-4 Models (2024-2025)
    { value: 'gpt-4o', label: 'GPT-4o (Latest & Most Capable - 2024)' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast & Cost-effective - 2024)' },
    { value: 'gpt-4-turbo-preview', label: 'GPT-4 Turbo Preview (Latest Features - 2024)' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (High Performance)' },
    { value: 'gpt-4', label: 'GPT-4 (Standard)' },
    { value: 'gpt-4-32k', label: 'GPT-4 32K (Long Context)' },
    { value: 'gpt-4-1106-preview', label: 'GPT-4 Turbo 1106 Preview (Latest)' },
    { value: 'gpt-4-0125-preview', label: 'GPT-4 Turbo 0125 Preview (Latest)' },
    { value: 'gpt-4-0613', label: 'GPT-4 0613 (Stable)' },
    // Latest GPT-3.5 Models (2024-2025)
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Fast & Reliable)' },
    { value: 'gpt-3.5-turbo-16k', label: 'GPT-3.5 Turbo 16K (Extended Context)' },
    { value: 'gpt-3.5-turbo-instruct', label: 'GPT-3.5 Turbo Instruct (Instruction Following)' },
    { value: 'gpt-3.5-turbo-1106', label: 'GPT-3.5 Turbo 1106 (Latest Version)' },
    { value: 'gpt-3.5-turbo-0125', label: 'GPT-3.5 Turbo 0125 (Latest Version)' },
    { value: 'gpt-3.5-turbo-0613', label: 'GPT-3.5 Turbo 0613 (Stable)' },
    // Specialized OpenAI Models
    { value: 'gpt-4-vision-preview', label: 'GPT-4 Vision (Image + Text)' },
    { value: 'gpt-4o-2024-05-13', label: 'GPT-4o 2024-05-13 (Latest Vision)' },
    { value: 'gpt-4o-mini-2024-07-18', label: 'GPT-4o Mini 2024-07-18 (Latest)' },
    { value: 'gpt-4-all', label: 'GPT-4 All (Multimodal)' },
    { value: 'gpt-4o-2024-07-18', label: 'GPT-4o 2024-07-18 (Latest Stable)' },
    { value: 'gpt-4o-mini-2024-07-18', label: 'GPT-4o Mini 2024-07-18 (Latest Stable)' },
    // DALL-E Models (Image Generation)
    { value: 'dall-e-3', label: 'DALL-E 3 (Latest Image Generation)' },
    { value: 'dall-e-2', label: 'DALL-E 2 (Image Generation)' },
    // Whisper Models (Speech Recognition)
    { value: 'whisper-1', label: 'Whisper (Speech Recognition)' },
    // Embedding Models
    { value: 'text-embedding-3-large', label: 'Text Embedding 3 Large (Latest)' },
    { value: 'text-embedding-3-small', label: 'Text Embedding 3 Small (Fast)' },
    { value: 'text-embedding-ada-002', label: 'Text Embedding Ada 002 (Stable)' },
    // Moderation Models
    { value: 'text-moderation-latest', label: 'Text Moderation Latest' },
    { value: 'text-moderation-stable', label: 'Text Moderation Stable' }
  ];

  const contentTypes = [
    { value: 'blog_post', label: 'Blog Post', icon: FileText },
    { value: 'product_description', label: 'Product Description', icon: ShoppingCart },
    { value: 'seo_content', label: 'SEO Content', icon: Search },
    { value: 'email_template', label: 'Email Template', icon: Mail },
    { value: 'social_media', label: 'Social Media Post', icon: Share2 },
    { value: 'landing_page', label: 'Landing Page Copy', icon: FileText },
    { value: 'meta_description', label: 'Meta Description', icon: Search },
    { value: 'faq_content', label: 'FAQ Content', icon: Headphones },
    { value: 'testimonial', label: 'Testimonial', icon: Heart },
    { value: 'press_release', label: 'Press Release', icon: Share2 },
    { value: 'case_study', label: 'Case Study', icon: FileText },
    { value: 'whitepaper', label: 'Whitepaper', icon: FileText },
    { value: 'newsletter', label: 'Newsletter', icon: Mail },
    { value: 'ad_copy', label: 'Advertisement Copy', icon: Target },
    { value: 'video_script', label: 'Video Script', icon: FileText }
  ];

  const renderSettingsTab = () => (
    <div className="space-y-6">
      {/* OpenAI Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Bot className="w-5 h-5 mr-2" />
          OpenAI Configuration
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              OpenAI API Key
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={aiSettings.openai_api_key}
                onChange={(e) => handleSettingChange('openai_api_key', e.target.value)}
                placeholder="sk-..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={testConnection}
              disabled={testConnectionLoading || !aiSettings.openai_api_key}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {testConnectionLoading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <TestTube className="w-4 h-4 mr-2" />
              )}
              Test Connection
            </button>
          </div>
        </div>

        {testConnectionResult && (
          <div className={`mt-4 p-3 rounded-lg ${
            testConnectionResult.success 
              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
              : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
          }`}>
            <div className="flex items-center">
              {testConnectionResult.success ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              {testConnectionResult.message}
            </div>
            {testConnectionResult.error && (
              <p className="text-sm mt-1">{testConnectionResult.error}</p>
            )}
          </div>
        )}
      </div>

      {/* AI Chat Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <MessageSquare className="w-5 h-5 mr-2" />
          AI Chat Settings
        </h3>
        
        <div className="space-y-4">
          {/* Source Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="ai_chat_source_welcome_enabled"
                checked={aiSettings.ai_chat_source_welcome_enabled === 'true' || aiSettings.ai_chat_source_welcome_enabled === true}
                onChange={(e) => handleSettingChange('ai_chat_source_welcome_enabled', e.target.checked.toString())}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label htmlFor="ai_chat_source_welcome_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Use AI Chat Welcome Message only
              </label>
            </div>
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="ai_chat_source_site_content_enabled"
                checked={aiSettings.ai_chat_source_site_content_enabled === 'true' || aiSettings.ai_chat_source_site_content_enabled === true}
                onChange={(e) => handleSettingChange('ai_chat_source_site_content_enabled', e.target.checked.toString())}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label htmlFor="ai_chat_source_site_content_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Use Website & Admin Panel Content
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              AI Chat Welcome Message
            </label>
            <textarea
              value={aiSettings.ai_chat_welcome_message}
              onChange={(e) => handleSettingChange('ai_chat_welcome_message', e.target.value)}
              placeholder="Enter the welcome message for AI chat..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              This message will be used as the system prompt for the AI chat. You can include company information, services, pricing, etc.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                 AI Chat Model
               </label>
               <select
                 value={aiSettings.ai_chat_model}
                 onChange={(e) => handleSettingChange('ai_chat_model', e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
               >
                 <option value="auto">🔄 Auto (Smart Model Selection)</option>
                                     <optgroup label="Latest GPT-5 Models (2024-2025)">
                     <option value="gpt-5">GPT-5 (Latest & Most Advanced - 2024)</option>
                     <option value="gpt-5-turbo">GPT-5 Turbo (High Performance - 2024)</option>
                     <option value="gpt-5o">GPT-5o (Latest Omni Model - 2024)</option>
                     <option value="gpt-5o-mini">GPT-5o Mini (Fast & Cost-effective - 2024)</option>
                   </optgroup>
                   <optgroup label="Latest GPT-4 Models (2024-2025)">
                     <option value="gpt-4o">GPT-4o (Latest & Most Capable - 2024)</option>
                     <option value="gpt-4o-mini">GPT-4o Mini (Fast & Cost-effective - 2024)</option>
                     <option value="gpt-4-turbo-preview">GPT-4 Turbo Preview (Latest Features - 2024)</option>
                     <option value="gpt-4-turbo">GPT-4 Turbo (High Performance)</option>
                     <option value="gpt-4">GPT-4 (Standard)</option>
                     <option value="gpt-4-32k">GPT-4 32K (Long Context)</option>
                     <option value="gpt-4-1106-preview">GPT-4 Turbo 1106 Preview (Latest)</option>
                     <option value="gpt-4-0125-preview">GPT-4 Turbo 0125 Preview (Latest)</option>
                     <option value="gpt-4-0613">GPT-4 0613 (Stable)</option>
                     <option value="gpt-4o-2024-07-18">GPT-4o 2024-07-18 (Latest Stable)</option>
                     <option value="gpt-4o-mini-2024-07-18">GPT-4o Mini 2024-07-18 (Latest Stable)</option>
                   </optgroup>
                   <optgroup label="Latest GPT-3.5 Models (2024-2025)">
                     <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fast & Reliable)</option>
                     <option value="gpt-3.5-turbo-16k">GPT-3.5 Turbo 16K (Extended Context)</option>
                     <option value="gpt-3.5-turbo-instruct">GPT-3.5 Turbo Instruct (Instruction Following)</option>
                     <option value="gpt-3.5-turbo-1106">GPT-3.5 Turbo 1106 (Latest Version)</option>
                     <option value="gpt-3.5-turbo-0125">GPT-3.5 Turbo 0125 (Latest Version)</option>
                     <option value="gpt-3.5-turbo-0613">GPT-3.5 Turbo 0613 (Stable)</option>
                   </optgroup>
                   <optgroup label="Specialized OpenAI Models">
                     <option value="gpt-4-vision-preview">GPT-4 Vision (Image + Text)</option>
                     <option value="gpt-4o-2024-05-13">GPT-4o 2024-05-13 (Latest Vision)</option>
                     <option value="gpt-4o-mini-2024-07-18">GPT-4o Mini 2024-07-18 (Latest)</option>
                     <option value="gpt-4-all">GPT-4 All (Multimodal)</option>
                   </optgroup>
                   <optgroup label="DALL-E Models (Image Generation)">
                     <option value="dall-e-3">DALL-E 3 (Latest Image Generation)</option>
                     <option value="dall-e-2">DALL-E 2 (Image Generation)</option>
                   </optgroup>
                   <optgroup label="Whisper Models (Speech Recognition)">
                     <option value="whisper-1">Whisper (Speech Recognition)</option>
                   </optgroup>
                   <optgroup label="Embedding Models">
                     <option value="text-embedding-3-large">Text Embedding 3 Large (Latest)</option>
                     <option value="text-embedding-3-small">Text Embedding 3 Small (Fast)</option>
                     <option value="text-embedding-ada-002">Text Embedding Ada 002 (Stable)</option>
                   </optgroup>
                   <optgroup label="Moderation Models">
                     <option value="text-moderation-latest">Text Moderation Latest</option>
                     <option value="text-moderation-stable">Text Moderation Stable</option>
                   </optgroup>
                </select>
                
                {/* Auto Chat Model Indicator */}
                {aiSettings.ai_chat_model === 'auto' && (
                  <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center text-sm text-blue-700 dark:text-blue-300">
                      <Sparkles className="w-4 h-4 mr-2" />
                      <span className="font-medium">Auto Chat Model:</span>
                      <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-800 rounded text-xs">
                        {getAutoSelectedChatModel()}
                      </span>
                    </div>
                  </div>
                )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Max Tokens
              </label>
              <input
                type="number"
                value={aiSettings.ai_max_tokens}
                onChange={(e) => handleSettingChange('ai_max_tokens', e.target.value)}
                placeholder="1000"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
                     <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
               Temperature
             </label>
             <input
               type="number"
               step="0.1"
               min="0"
               max="2"
               value={aiSettings.ai_temperature}
               onChange={(e) => handleSettingChange('ai_temperature', e.target.value)}
               placeholder="0.7"
               className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
             />
             <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
               Controls randomness (0 = focused, 2 = creative)
             </p>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                 Max Conversations
               </label>
               <input
                 type="number"
                 value={aiSettings.ai_chat_max_conversations}
                 onChange={(e) => handleSettingChange('ai_chat_max_conversations', e.target.value)}
                 placeholder="50"
                 className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
               />
               <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                 Maximum number of conversations to store
               </p>
             </div>
             
             <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                 Session Timeout (minutes)
               </label>
               <input
                 type="number"
                 value={aiSettings.ai_chat_session_timeout}
                 onChange={(e) => handleSettingChange('ai_chat_session_timeout', e.target.value)}
                 placeholder="30"
                 className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
               />
               <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                 Chat session timeout in minutes
               </p>
             </div>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                 Response Timeout (seconds)
               </label>
               <input
                 type="number"
                 value={aiSettings.ai_response_timeout}
                 onChange={(e) => handleSettingChange('ai_response_timeout', e.target.value)}
                 placeholder="30"
                 className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
               />
               <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                 AI response timeout in seconds
               </p>
             </div>
             
             <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                 Rate Limit (per minute)
               </label>
               <input
                 type="number"
                 value={aiSettings.ai_rate_limit_per_minute}
                 onChange={(e) => handleSettingChange('ai_rate_limit_per_minute', e.target.value)}
                 placeholder="60"
                 className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
               />
               <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                 API rate limit per minute
               </p>
             </div>
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
               Backup Model
             </label>
             <select
               value={aiSettings.ai_backup_model}
               onChange={(e) => handleSettingChange('ai_backup_model', e.target.value)}
               className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
             >
               <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fast & Reliable)</option>
               <option value="gpt-4o-mini">GPT-4o Mini (Fast & Cost-effective)</option>
               <option value="gpt-4">GPT-4 (Standard)</option>
             </select>
             <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
               Backup model to use if primary model fails
             </p>
           </div>
        </div>
      </div>

      {/* Feature Toggles */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Zap className="w-5 h-5 mr-2" />
          AI Features
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                     {[
             { key: 'ai_enabled', label: 'Enable AI System', icon: Brain },
             { key: 'ai_chat_enabled', label: 'AI Chat', icon: MessageSquare },
             { key: 'ai_content_generation_enabled', label: 'Content Generation', icon: FileText },
             { key: 'ai_product_descriptions_enabled', label: 'Product Descriptions', icon: ShoppingCart },
             { key: 'ai_blog_writer_enabled', label: 'Blog Writer', icon: PenTool },
             { key: 'ai_customer_support_enabled', label: 'Customer Support', icon: Headphones },
             { key: 'ai_seo_optimization_enabled', label: 'SEO Optimization', icon: Search },
             { key: 'ai_image_generation_enabled', label: 'Image Generation', icon: Image },
             { key: 'ai_translation_enabled', label: 'Translation', icon: Globe },
             { key: 'ai_sentiment_analysis_enabled', label: 'Sentiment Analysis', icon: Heart },
             { key: 'ai_personalization_enabled', label: 'Personalization', icon: Users },
             { key: 'ai_recommendations_enabled', label: 'Recommendations', icon: Target },
             { key: 'ai_analytics_enabled', label: 'AI Analytics', icon: BarChart3 },
             { key: 'ai_automation_enabled', label: 'Automation', icon: Zap },
             { key: 'ai_fallback_enabled', label: 'Fallback Mode', icon: RefreshCw },
             { key: 'ai_logging_enabled', label: 'AI Logging', icon: FileText },
             { key: 'ai_debug_mode', label: 'Debug Mode', icon: TestTube }
           ].map(feature => (
            <div key={feature.key} className="flex items-center space-x-3">
              <input
                type="checkbox"
                id={feature.key}
                checked={aiSettings[feature.key] === 'true' || aiSettings[feature.key] === true}
                onChange={(e) => handleSettingChange(feature.key, e.target.checked.toString())}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label htmlFor={feature.key} className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <feature.icon className="w-4 h-4 mr-2" />
                {feature.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveAISettings}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {loading ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Settings
        </button>
      </div>

      {saveMessage && (
        <div className={`p-4 rounded-lg ${
          saveStatus === 'success' 
            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
            : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
        }`}>
          <div className="flex items-center">
            {saveStatus === 'success' ? (
              <CheckCircle className="w-4 h-4 mr-2" />
            ) : (
              <AlertTriangle className="w-4 h-4 mr-2" />
            )}
            {saveMessage}
          </div>
        </div>
      )}
    </div>
  );

  const renderImageGenerationTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Image className="w-5 h-5 mr-2" />
          Image Generation
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Image Prompt
            </label>
            <textarea
              value={imageGenerationForm.prompt}
              onChange={(e) => {
                setImageGenerationForm(prev => ({ ...prev, prompt: e.target.value }));
                setImageValidationMessage('');
              }}
              placeholder="Describe the image you want to generate..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Be descriptive and specific about what you want to see in the image
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Model
              </label>
              <select
                value={imageGenerationForm.model}
                onChange={(e) => setImageGenerationForm(prev => ({ ...prev, model: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="dall-e-3">DALL-E 3 (Latest & Highest Quality)</option>
                <option value="dall-e-2">DALL-E 2 (Fast & Cost-effective)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Size
              </label>
              <select
                value={imageGenerationForm.size}
                onChange={(e) => setImageGenerationForm(prev => ({ ...prev, size: e.target.value, customSize: '' }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                {imageGenerationForm.model === 'dall-e-3' ? (
                  <>
                    <option value="1024x1024">1024x1024 (Square)</option>
                    <option value="1792x1024">1792x1024 (Landscape)</option>
                    <option value="1024x1792">1024x1792 (Portrait)</option>
                    <option value="custom">Custom Size</option>
                  </>
                ) : (
                  <>
                    <option value="256x256">256x256 (Small)</option>
                    <option value="512x512">512x512 (Medium)</option>
                    <option value="1024x1024">1024x1024 (Large)</option>
                    <option value="custom">Custom Size</option>
                  </>
                )}
              </select>
            </div>
            
            {imageGenerationForm.size === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Size (e.g., 1024x1024)
                </label>
                <input
                  type="text"
                  value={imageGenerationForm.customSize}
                  onChange={(e) => {
                    setImageGenerationForm(prev => ({ ...prev, customSize: e.target.value }));
                    setImageValidationMessage('');
                  }}
                  placeholder={imageGenerationForm.model === 'dall-e-3' ? '1024x1024, 1792x1024, 1024x1792' : '256x256, 512x512, 1024x1024'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 space-y-1">
                  {imageGenerationForm.model === 'dall-e-3' ? (
                    <>
                      <p>DALL-E 3 supports custom sizes with these aspect ratios:</p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li>1:1 (Square) - e.g., 1024x1024, 1536x1536</li>
                        <li>16:9 (Landscape) - e.g., 1792x1024, 1920x1080</li>
                        <li>9:16 (Portrait) - e.g., 1024x1792, 1080x1920</li>
                      </ul>
                      <p className="text-blue-600 dark:text-blue-400">Dimensions must be between 1-2048 pixels</p>
                    </>
                  ) : (
                    <>
                      <p>DALL-E 2 supports custom sizes with these restrictions:</p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li>Square aspect ratio only (width = height)</li>
                        <li>Examples: 256x256, 512x512, 1024x1024, 1536x1536</li>
                      </ul>
                      <p className="text-blue-600 dark:text-blue-400">Dimensions must be between 1-2048 pixels</p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {imageGenerationForm.model === 'dall-e-3' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quality
                </label>
                <select
                  value={imageGenerationForm.quality}
                  onChange={(e) => setImageGenerationForm(prev => ({ ...prev, quality: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="standard">Standard (Balanced)</option>
                  <option value="hd">HD (Higher Quality, More Detail)</option>
                </select>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  HD quality provides more detailed and refined images
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Style
                </label>
                <select
                  value={imageGenerationForm.style}
                  onChange={(e) => setImageGenerationForm(prev => ({ ...prev, style: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="vivid">Vivid (More Dramatic & Colorful)</option>
                  <option value="natural">Natural (More Photorealistic)</option>
                </select>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Vivid creates more dramatic, colorful images; Natural creates more realistic ones
                </p>
              </div>
            </div>
          )}
          
          {imageValidationMessage && (
            <div className={`p-3 rounded-lg text-sm ${
              imageValidationMessage.includes('Error') || imageValidationMessage.includes('Invalid') 
                ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' 
                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
            }`}>
              {imageValidationMessage}
            </div>
          )}
          
          <button
            onClick={generateImage}
            disabled={loading || !imageGenerationForm.prompt.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Image className="w-4 h-4 mr-2" />
            )}
            Generate Image
          </button>
        </div>
      </div>

      {generatedImage && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
            Generated Image
          </h3>
          
          <div className="space-y-4">
            <div className="flex justify-center">
              <img
                src={generatedImage.image_url}
                alt="Generated"
                className="max-w-full h-auto rounded-lg shadow-lg"
                style={{ maxHeight: '500px' }}
              />
            </div>
            
            {generatedImage.revised_prompt && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Revised Prompt:
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {generatedImage.revised_prompt}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Model:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">{generatedImage.model}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Size:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">{generatedImage.size}</span>
              </div>
              {generatedImage.quality && (
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Quality:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{generatedImage.quality}</span>
                </div>
              )}
              {generatedImage.style && (
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Style:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{generatedImage.style}</span>
                </div>
              )}
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => window.open(generatedImage.image_url, '_blank')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Open Full Size
              </button>
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = generatedImage.image_url;
                  link.download = 'generated-image.png';
                  link.click();
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Download
              </button>
              <button
                onClick={() => setGeneratedImage(null)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderContentGenerationTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Content Generation
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Content Type
            </label>
            <select
              value={contentGenerationForm.type}
              onChange={(e) => setContentGenerationForm(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              {contentTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
                     <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
               AI Model
             </label>
                           <select
                value={aiSettings.ai_content_model}
                onChange={(e) => handleSettingChange('ai_content_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="auto">🔄 Auto (Smart Model Selection)</option>
                <optgroup label="Latest GPT-5 Models (Best Quality - 2024-2025)">
                  <option value="gpt-5">GPT-5 (Latest & Most Advanced - 2024)</option>
                  <option value="gpt-5-turbo">GPT-5 Turbo (High Performance - 2024)</option>
                  <option value="gpt-5o">GPT-5o (Latest Omni Model - 2024)</option>
                  <option value="gpt-5o-mini">GPT-5o Mini (Fast & Cost-effective - 2024)</option>
                </optgroup>
                <optgroup label="Latest GPT-4 Models (Best Quality - 2024-2025)">
                  <option value="gpt-4o">GPT-4o (Latest & Most Capable - 2024)</option>
                  <option value="gpt-4o-mini">GPT-4o Mini (Fast & Cost-effective - 2024)</option>
                  <option value="gpt-4-turbo-preview">GPT-4 Turbo Preview (Latest Features - 2024)</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo (High Performance)</option>
                  <option value="gpt-4">GPT-4 (Standard)</option>
                  <option value="gpt-4-32k">GPT-4 32K (Long Context)</option>
                  <option value="gpt-4-1106-preview">GPT-4 Turbo 1106 Preview (Latest)</option>
                  <option value="gpt-4-0125-preview">GPT-4 Turbo 0125 Preview (Latest)</option>
                  <option value="gpt-4-0613">GPT-4 0613 (Stable)</option>
                  <option value="gpt-4o-2024-07-18">GPT-4o 2024-07-18 (Latest Stable)</option>
                  <option value="gpt-4o-mini-2024-07-18">GPT-4o Mini 2024-07-18 (Latest Stable)</option>
                </optgroup>
                <optgroup label="Latest GPT-3.5 Models (Fast & Affordable - 2024-2025)">
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fast & Reliable)</option>
                  <option value="gpt-3.5-turbo-16k">GPT-3.5 Turbo 16K (Extended Context)</option>
                  <option value="gpt-3.5-turbo-instruct">GPT-3.5 Turbo Instruct (Instruction Following)</option>
                  <option value="gpt-3.5-turbo-1106">GPT-3.5 Turbo 1106 (Latest Version)</option>
                  <option value="gpt-3.5-turbo-0125">GPT-3.5 Turbo 0125 (Latest Version)</option>
                  <option value="gpt-3.5-turbo-0613">GPT-3.5 Turbo 0613 (Stable)</option>
                </optgroup>
                <optgroup label="Specialized OpenAI Models">
                  <option value="gpt-4-vision-preview">GPT-4 Vision (Image + Text)</option>
                  <option value="gpt-4o-2024-05-13">GPT-4o 2024-05-13 (Latest Vision)</option>
                  <option value="gpt-4o-mini-2024-07-18">GPT-4o Mini 2024-07-18 (Latest)</option>
                  <option value="gpt-4-all">GPT-4 All (Multimodal)</option>
                </optgroup>
                <optgroup label="DALL-E Models (Image Generation)">
                  <option value="dall-e-3">DALL-E 3 (Latest Image Generation)</option>
                  <option value="dall-e-2">DALL-E 2 (Image Generation)</option>
                </optgroup>
                <optgroup label="Whisper Models (Speech Recognition)">
                  <option value="whisper-1">Whisper (Speech Recognition)</option>
                </optgroup>
                <optgroup label="Embedding Models">
                  <option value="text-embedding-3-large">Text Embedding 3 Large (Latest)</option>
                  <option value="text-embedding-3-small">Text Embedding 3 Small (Fast)</option>
                  <option value="text-embedding-ada-002">Text Embedding Ada 002 (Stable)</option>
                </optgroup>
                <optgroup label="Moderation Models">
                  <option value="text-moderation-latest">Text Moderation Latest</option>
                  <option value="text-moderation-stable">Text Moderation Stable</option>
                </optgroup>
             </select>
                         <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
               Choose the AI model for content generation. GPT-4 models provide better quality but cost more.
             </p>
             
             {/* Auto Model Indicator */}
             {aiSettings.ai_content_model === 'auto' && (
               <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                 <div className="flex items-center text-sm text-blue-700 dark:text-blue-300">
                   <Sparkles className="w-4 h-4 mr-2" />
                   <span className="font-medium">Auto Model Selection:</span>
                   <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-800 rounded text-xs">
                     {getAutoSelectedModel(contentGenerationForm.type, contentGenerationForm.prompt)}
                   </span>
                 </div>
                 <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                   Model will be automatically selected based on content type and requirements
                 </p>
               </div>
             )}
          </div>
          
                     <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
               Prompt
             </label>
             <textarea
               value={contentGenerationForm.prompt}
               onChange={(e) => setContentGenerationForm(prev => ({ ...prev, prompt: e.target.value }))}
               placeholder="Describe what content you want to generate..."
               rows={4}
               className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
               Context (Optional)
             </label>
             <textarea
               value={contentGenerationForm.context}
               onChange={(e) => setContentGenerationForm(prev => ({ ...prev, context: e.target.value }))}
               placeholder="Add any additional context, keywords, or requirements..."
               rows={3}
               className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
             />
             <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
               Provide additional context to help generate more relevant content
             </p>
           </div>
          
          <button
            onClick={generateContent}
            disabled={loading || !contentGenerationForm.prompt.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Generate Content
          </button>
        </div>
      </div>

      {generatedContent && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
            Generated Content
          </h3>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
              {generatedContent}
            </pre>
          </div>
          
          <div className="mt-4 flex space-x-2">
            <button
              onClick={() => navigator.clipboard.writeText(generatedContent)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Copy to Clipboard
            </button>
            <button
              onClick={() => setGeneratedContent('')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderFeaturesTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:p-6">
        {/* AI Chat Feature */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Chat</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Intelligent chatbot for customer support and inquiries
          </p>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Real-time conversations
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Context awareness
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Multi-language support
            </div>
          </div>
        </div>

        {/* Content Generation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Content Generation</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            AI-powered content creation for blogs, products, and marketing
          </p>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Blog post writing
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Product descriptions
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              SEO optimization
            </div>
          </div>
        </div>

        {/* Sentiment Analysis */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Sentiment Analysis</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Analyze customer feedback and social media sentiment
          </p>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Emotion detection
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Trend analysis
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Customer insights
            </div>
          </div>
        </div>

        {/* Translation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Translation</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Multi-language translation for global reach
          </p>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Multiple languages
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Context preservation
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Real-time translation
            </div>
          </div>
        </div>

        {/* Personalization */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Personalization</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Personalized user experiences and recommendations
          </p>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              User preferences
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Behavior analysis
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Dynamic content
            </div>
          </div>
        </div>

        {/* Analytics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Analytics</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Advanced analytics with AI-powered insights
          </p>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Trend prediction
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Anomaly detection
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Performance insights
            </div>
          </div>
        </div>

        {/* Image Generation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <Image className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Image Generation</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            AI-powered image generation for marketing and content
          </p>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Custom artwork
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Marketing visuals
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Product mockups
            </div>
          </div>
        </div>

        {/* Automation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Automation</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Automated workflows and task management
          </p>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Email automation
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Content scheduling
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Social media posting
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <Target className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Smart Recommendations</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            AI-powered product and content recommendations
          </p>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Product suggestions
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Content recommendations
            </div>
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Personalized offers
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'settings':
        return renderSettingsTab();
      case 'content':
        return renderContentGenerationTab();
      case 'image':
        return renderImageGenerationTab();
      case 'features':
        return renderFeaturesTab();
      default:
        return renderSettingsTab();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Brain className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
            AI Management
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Configure and manage AI features for your website
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex overflow-x-auto scrollbar-hide px-4 sm:px-6" aria-label="Tabs">
              <div className="flex space-x-2 sm:space-x-8 min-w-max">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-4 sm:p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIManagement;
